// frontend/src/components/failedAlert.tsx
import React, { useMemo } from "react";
import { Alert, Code, Stack, Text } from "@mantine/core";

type Props = {
  title?: string;
  message?: string;

  /**
   * Typically RTK Query error, but can be anything.
   * We try to extract something meaningful and show it in a compact way.
   */
  error?: unknown;

  /**
   * If false, nothing is rendered. Default: true
   */
  show?: boolean;
};

const tryStringify = (v: unknown): string | undefined => {
  if (v == null) return undefined;
  if (typeof v === "string") return v;
  if (v instanceof Error) return v.message || String(v);

  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const extractBestErrorText = (error: unknown): string | undefined => {
  // Common RTK Query shapes:
  // { status, data } OR { error: "..." }
  const e = error as any;

  const dataText = tryStringify(e?.data);
  const errText = tryStringify(e?.error);
  const msgText = tryStringify(e?.message);

  // Prefer structured backend response
  return dataText ?? errText ?? msgText ?? tryStringify(error);
};

export const FailedAlert: React.FC<Props> = ({
  title = "Noe gikk galt",
  message = "Klarte ikke å hente data.",
  error,
  show = true,
}) => {
  const details = useMemo(() => extractBestErrorText(error), [error]);

  if (!show) return null;

  return (
    <Alert color="red" variant="light" title={title}>
      <Stack gap={6}>
        <Text size="sm">{message}</Text>

        {details && (
          <Code block style={{ maxHeight: 200, overflow: "auto" }}>
            {details}
          </Code>
        )}
      </Stack>
    </Alert>
  );
};
